import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import MyProfileDetails from "./MyProfileDetails"; // Import your new component

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);

  const location = useLocation();
  const { profile, navItems } = useSelector((state) => state.portfolio);

  const activeNavItem = useMemo(() => {
    return (
      navItems.find((item) => item.path === location.pathname) || navItems[0]
    );
  }, [location.pathname, navItems]);

  const profileImage = hoveredImage || activeNavItem?.image;

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleProfileClick = (e) => {
    e.preventDefault();
    setShowOverlay(true);
  };

  const navClass = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${isActive
      ? "bg-white text-slate-950"
      : "text-slate-300 hover:bg-white hover:text-slate-950"
    }`;

  const mobileNavClass = ({ isActive }) =>
    `rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${isActive
      ? "bg-white text-slate-950"
      : "text-slate-300 hover:bg-white hover:text-slate-950"
    }`;

  return (
    <>
      <nav className="fixed left-0 top-0 z-50 w-full border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
          <NavLink
            to="/"
            onClick={closeMenu}
            className="group flex items-center gap-3 relative"
            onMouseEnter={() => setHoveredImage(navItems[0]?.image)}
            onMouseLeave={() => setHoveredImage(null)}
          >
            <div className="relative">
              <div
                onClick={handleProfileClick}
                className="relative z-10 h-12 w-12 cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg shadow-white/10 transition-all duration-300 group-hover:rotate-6 group-hover:scale-105 group-hover:border-indigo-400/50 group-hover:shadow-[0_0_15px_rgba(99,102,241,0.5)]"
              >
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt={activeNavItem?.label || profile.name}
                    className="h-full w-full object-cover transition-all duration-300"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm font-bold text-slate-950">
                    {profile.shortName}
                  </div>
                )}
              </div>

              <div className="pointer-events-none absolute -bottom-8 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-lg bg-indigo-500 px-2.5 py-1 text-[10px] font-bold text-white opacity-0 shadow-lg shadow-indigo-500/20 transition-all duration-300 ease-out group-hover:translate-y-2 group-hover:opacity-100">
                <span>Click me!</span>
                <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-indigo-500"></div>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold tracking-wide text-white">
                {profile.name}
              </p>
              <p className="text-xs text-slate-400">
                {activeNavItem?.label || "Portfolio"}
              </p>
            </div>
          </NavLink>

          <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={navClass}
                onMouseEnter={() => setHoveredImage(item.image)}
                onMouseLeave={() => setHoveredImage(null)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <button
            type="button"
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 md:hidden"
          >
            <span className="relative h-5 w-5">
              <span
                className={`absolute left-0 top-1 block h-0.5 w-5 rounded bg-white transition-all duration-300 ${isMenuOpen ? "translate-y-1.5 rotate-45" : ""
                  }`}
              />
              <span
                className={`absolute left-0 top-2.5 block h-0.5 w-5 rounded bg-white transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""
                  }`}
              />
              <span
                className={`absolute left-0 top-4 block h-0.5 w-5 rounded bg-white transition-all duration-300 ${isMenuOpen ? "-translate-y-1.5 -rotate-45" : ""
                  }`}
              />
            </span>
          </button>
        </div>

        {isMenuOpen && (
          <div className="animate-fade-down border-t border-white/10 bg-slate-950/95 px-5 pb-5 pt-2 backdrop-blur-xl md:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={closeMenu}
                  className={mobileNavClass}
                  onMouseEnter={() => setHoveredImage(item.image)}
                  onMouseLeave={() => setHoveredImage(null)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {showOverlay && (
        <MyProfileDetails
          setShowOverlay={setShowOverlay}
          profile={profile}
          profileImage={profileImage}
        />
      )}
    </>
  );
}

export default Navbar;